// Dashboard-Startseite (Refined): personalisierter Kopf + zwei Blöcke —
// Block 1 „Meine Arbeit" (Gruppierung der drei Arbeits-Karten) und darunter
// Block 2, ein 2-Spalten-Raster mit sechs Karten (alle echt).
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
    Layer20Regular,
    Add20Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../../context/AuthContext";
import { useResponsive } from "../../hooks/useResponsive";
import { vorgangService, type VorgangDto, type TodoDto } from "../../services/vorgangService";
import type { ScopeCtx } from "../../utils/vorgangScope";
import { openVorgangCreateFlow } from "../vorgang/create/VorgangCreateFlowWindow";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { REFINED, BRAND } from "../risikoanalyseStep/tokens";
import { greeting, todayEyebrow, DashGrid, SectionGroup } from "./DashboardShared";
import { MeineArbeitCard } from "./MeineArbeitCard";
import { TermineWiedervorlagenCard } from "./TermineWiedervorlagenCard";
import { MitteilungszentrumCard } from "./MitteilungszentrumCard";
import { SchaedenCard } from "./SchaedenCard";
import { MeineKundenCard } from "./MeineKundenCard";
import { CourteInternCard } from "./CourteInternCard";
import { AnAbwesenheitenCard } from "./AnAbwesenheitenCard";
import { RisikenCard } from "./RisikenCard";
import { GeburtstageCard } from "./GeburtstageCard";
import { KundenverbindungenCard } from "./KundenverbindungenCard";

const firstName = (user: any): string => {
    if (user?.firstName) return String(user.firstName);
    const n = user?.name || user?.displayName;
    if (typeof n === "string" && n.trim()) return n.trim().split(/\s+/)[0];
    if (typeof user?.email === "string") return user.email.split("@")[0];
    return "";
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { openWindow } = useWindowManager();
    const { isPhone } = useResponsive();
    const { user, teammates, teams } = useAuth();

    const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
    const [todos, setTodos] = useState<TodoDto[]>([]);
    // T-153: Die Vorgänge-Karte sagt „Lädt …“, bis BEIDE Listen da sind — ein
    // Vorgang landet auch über seine To-dos in „Heute“/„Woche“.
    const [vorgaengeLaden, setVorgaengeLaden] = useState(true);
    const [todosLaden, setTodosLaden] = useState(true);

    const scopeCtx: ScopeCtx = useMemo(
        () => ({ userId: user?.id ?? null, teammates: teammates ?? [], teamIds: (teams ?? []).map((t) => t.id) }),
        [user?.id, teammates, teams],
    );

    const load = useCallback(() => {
        // M4: kein force — den geteilten swrCache nutzen (dedupt mit useNavBadges).
        setVorgaengeLaden(true);
        setTodosLaden(true);
        vorgangService.getAllVorgaenge({}).then(setVorgaenge).catch(() => setVorgaenge([]))
            .finally(() => setVorgaengeLaden(false));
        // Punkt 3: light — ohne notes/assigned*-Relationen (GET /todo?draft=all war ~5 s).
        vorgangService.getAllTodos({}, true).then(setTodos).catch(() => setTodos([]))
            .finally(() => setTodosLaden(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const name = firstName(user);

    return (
        <div style={{ background: "#f6efe4", minHeight: "100%", width: "100%" }}>
            <div
                style={{
                    // Fluid-Breite: Karten nutzen breite Monitore aus; weicher
                    // Lesbarkeits-Deckel bei 1720px (statt vorher fixer 1180px).
                    maxWidth: 1720,
                    margin: "0 auto",
                    // Phone: schmaleres Außen-Padding + kleinerer Block-Abstand, damit
                    // der Inhalt auf 375px nutzt, ohne horizontal zu überlaufen (AP-R).
                    padding: isPhone ? "18px 14px 32px" : "28px 32px 48px",
                    fontFamily: REFINED.fontUI,
                    display: "flex",
                    flexDirection: "column",
                    gap: isPhone ? 16 : 24,
                }}
            >
                {/* Kopf */}
                <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ color: REFINED.ink2, fontSize: "0.9rem", fontWeight: 600, textTransform: "capitalize" }}>
                            {todayEyebrow()}
                        </div>
                        <h1 style={{ margin: "4px 0 0", fontFamily: BRAND.fontAccent, fontWeight: 600, fontSize: isPhone ? "1.8rem" : "2.4rem", color: REFINED.ink, lineHeight: 1.05, overflowWrap: "anywhere" }}>
                            {greeting()}{name ? `, ${name}` : ""}
                        </h1>
                    </div>
                    {/* Button-Gruppe bricht auf dem Phone um (nimmt volle Breite),
                        damit die beiden Aktionen nicht über 375px hinauslaufen. */}
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: isPhone ? 8 : 12, width: isPhone ? "100%" : "auto" }}>
                        <button
                            type="button"
                            onClick={() => navigate("/vorgaenge")}
                            style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                                // Phone: füllt zusammen mit dem Geschwister-Button die Zeile
                                // (flex:1) und hält ein 44px-Touch-Target ein.
                                flex: isPhone ? 1 : "none", minHeight: isPhone ? 44 : undefined,
                                padding: "11px 18px", borderRadius: 12, cursor: "pointer",
                                border: `1px solid ${REFINED.line}`, backgroundColor: REFINED.white,
                                color: REFINED.ink, fontFamily: REFINED.fontUI, fontSize: "0.92rem", fontWeight: 700,
                                whiteSpace: "nowrap",
                                boxShadow: "0 1px 3px rgba(58,12,49,0.08)",
                            }}
                        >
                            <Layer20Regular /> Alle Vorgänge
                        </button>
                        <button
                            type="button"
                            onClick={() => openVorgangCreateFlow(openWindow, {})}
                            style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                                flex: isPhone ? 1 : "none", minHeight: isPhone ? 44 : undefined,
                                padding: "11px 18px", borderRadius: 12, cursor: "pointer", border: "none",
                                backgroundColor: REFINED.aubDeep, color: REFINED.white,
                                fontFamily: REFINED.fontUI, fontSize: "0.92rem", fontWeight: 700,
                                whiteSpace: "nowrap",
                                boxShadow: REFINED.btnShadow,
                            }}
                        >
                            <Add20Regular /> Neuer Vorgang
                        </button>
                    </div>
                </header>

                {/* BLOCK 1 — Meine Arbeit (Gruppierung der 3 Arbeits-Karten) */}
                <SectionGroup title="Meine Arbeit" icon={<Layer20Regular />}>
                    <MeineArbeitCard
                        vorgaenge={vorgaenge}
                        todos={todos}
                        scopeCtx={scopeCtx}
                        onChanged={load}
                        loading={vorgaengeLaden || todosLaden}
                    />
                    <MitteilungszentrumCard />
                    <TermineWiedervorlagenCard />
                </SectionGroup>

                {/* BLOCK 2 — 2-Spalten-Karten-Raster (3 Zeilen × 2 Spalten) */}
                <DashGrid>
                    <AnAbwesenheitenCard />
                    <CourteInternCard />
                    <SchaedenCard />
                    <RisikenCard />
                    <MeineKundenCard />
                    <GeburtstageCard />
                </DashGrid>

                {/* Ganz unten: alle Kundenverbindungen, Klick → Bearbeiten-Popup
                    (User 28.09.2026, vorher ein Knopf in der Kontakttabelle). */}
                <KundenverbindungenCard />
            </div>

        </div>
    );
};

export default Dashboard;
